<script setup lang="ts">
import { InvoiceDetails } from '@/Pejer/types/invoice'
import DataStateTableCell from 'pejer/src/components/DataState/DataStateTableCell.vue'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'pejer/src/components/ui/table'
import { CustomerTypeEnum } from 'pejer/src/tables/Global'
import { DataState } from 'pejer/src/types/types'
import { computed } from 'vue'

const props = defineProps<{
  invoiceDetails: InvoiceDetails | undefined
  dataState: DataState
  sum?: number
  dueDate?: string
}>()

const grossAmount = computed(
  () =>
    (props.invoiceDetails?.orderDetails?.productLines.reduce(
      (acc, line) => acc + line.grossCost,
      0
    ) || 0) + (props.invoiceDetails?.orderDetails?.grossShippingCost || 0)
)

// If dueDate is present we show it. Prioritize the prop date if present.
const displayDueDate = computed(() => props.dueDate ?? props.invoiceDetails?.invoice?.dueDate)

const isCredited = computed(() => {
  return (
    props.invoiceDetails?.paymentStatus === 'CancelledPayment' ||
    props.invoiceDetails?.paymentStatus === 'RefundedPayment' ||
    props.invoiceDetails?.paymentStatus === 'RefundInitiated'
  )
})
</script>
<template>
  <!-- Faktura-info -->
  <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 text-sm break-inside-avoid">
    <div>
      <Table class="w-full rounded-t-lg">
        <TableHeader class="bg-slate-50">
          <TableRow class="rounded-t-lg">
            <TableHead colspan="2" class="p-2 text-left">Fakturert til:</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell class="p-2 font-semibold">
              <span
                v-if="
                  !dataState.isLoading.value &&
                  !dataState.isError.value &&
                  invoiceDetails?.invoice.customerType === CustomerTypeEnum.Business
                "
              >
                Firmanavn:
              </span>
              <span v-else>Kundenavn:</span>
            </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm w-full">{{
              invoiceDetails.invoice.customerName
            }}</DataStateTableCell>
          </TableRow>
          <TableRow
            v-if="
              !dataState.isLoading.value &&
              !dataState.isError.value &&
              invoiceDetails?.invoice.customerType === CustomerTypeEnum.Business
            "
          >
            <TableCell class="p-2 font-semibold"> Org.nr.: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">
              {{ invoiceDetails?.invoice.customerOrganizationNumber }}
            </DataStateTableCell>
          </TableRow>
          <TableRow>
            <TableCell class="p-2 font-semibold"> Adresse: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm"
              >{{ invoiceDetails.invoice.customerAddress }},
              {{ invoiceDetails.invoice.customerPostalCode }}
              {{ invoiceDetails.invoice.customerCity }}</DataStateTableCell
            >
          </TableRow>
          <TableRow>
            <TableCell class="p-2 font-semibold"> E-post: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">
              <a
                :href="`mailto:${invoiceDetails.invoice.customerEmail}`"
                class="text-blue-600 hover:underline"
                >{{ invoiceDetails.invoice.customerEmail }}</a
              >
            </DataStateTableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
    <div class="min-w-fit w-full h-fit">
      <Table class="w-full rounded-t-lg">
        <TableHeader>
          <TableRow class="rounded-t-lg bg-slate-50">
            <TableHead colspan="2" class="p-2 text-left">Fakturadetaljer:</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell class="p-2 font-semibold w-fit"> Fakturanr: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm w-full">{{
              invoiceDetails.invoiceReferenceID
            }}</DataStateTableCell>
          </TableRow>
          <TableRow>
            <TableCell class="p-2 font-semibold w-fit"> Fakturadato: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">{{
              new Date(invoiceDetails.invoice.insertTime).toLocaleDateString('nb-NO')
            }}</DataStateTableCell>
          </TableRow>
          <TableRow
            :class="{
              // have print reset this to normal
              'text-slate-400 line-through print:text-slate-700 print:no-underline': isCredited,
            }"
          >
            <TableCell class="p-2 font-semibold w-fit"> Forfallsdato: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">{{
              displayDueDate ? new Date(displayDueDate).toLocaleDateString('nb-NO') : 'Snarest'
            }}</DataStateTableCell>
          </TableRow>
          <TableRow
            :class="{
              'text-slate-400 line-through print:text-slate-700 print:no-underline': isCredited,
            }"
          >
            <TableCell class="p-2 font-semibold w-fit"> KID: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">{{
              invoiceDetails.invoice.kid
            }}</DataStateTableCell>
          </TableRow>
          <TableRow
            :class="{
              'text-slate-400 line-through print:text-slate-700 print:no-underline': isCredited,
            }"
          >
            <TableCell class="p-2 font-semibold w-fit"> Kontonummer: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">{{
              invoiceDetails.invoice.accountNumber
            }}</DataStateTableCell>
          </TableRow>
          <TableRow>
            <TableCell class="p-2 font-semibold w-fit"> Beløp: </TableCell>
            <DataStateTableCell :dataState="dataState" class="p-2 text-sm">
              {{ !isCredited ? ((sum ?? grossAmount) / 100).toFixed(2) : '0.00' }}
              kr
            </DataStateTableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>
